import { Badge, Box, Button, Group, Modal, Stack, Table, Text } from '@mantine/core'
import { IconAlertTriangle } from '@tabler/icons-react'
import React from 'react'
import {
	classifyDictationLength,
	countWords,
	DICTATION_LENGTH_STATUS_LABEL,
	DICTATION_MAX_WORDS,
	DICTATION_MIN_WORDS,
	dictationLengthHeading,
	DictationLengthIssue,
	dictationSkippedLabel,
	dictationWordCountLabel,
	formatWordCount,
} from './dictationLength'

const WARNING_COLOR = '#c2410c'

/** The live count under the English text field; orange while the text is outside the range. */
export function DictationWordCounter({ text }: { text: string }) {
	const wordCount = countWords(text)
	const outside = classifyDictationLength(wordCount) !== 'ok'
	return (
		<Text
			size="xs"
			fw={outside ? 600 : 400}
			color={outside ? WARNING_COLOR : 'dimmed'}
			data-testid="dictation-word-counter"
			data-outside-range={outside ? 'true' : 'false'}
		>
			{dictationWordCountLabel(wordCount)}
		</Text>
	)
}

export const DICTATION_SKIPPED_HINT =
	'The server skips this lesson when it picks the topic of a day. Fix it and it is back in the rotation by itself.'

/**
 * The red "Skipped: <reason>" label of a Listening lesson in the lesson list (UD-545). Shown
 * only when the server sent a reason with the lesson.
 */
export function DictationSkippedLabel({ reason }: { reason?: unknown }) {
	const label = dictationSkippedLabel(reason)
	if (!label) return null
	return (
		<Box mt={4}>
			<Badge
				color="red"
				variant="light"
				radius="sm"
				title={DICTATION_SKIPPED_HINT}
				data-testid="dictation-skipped-label"
				styles={{
					root: { height: 'auto', padding: '3px 8px', textTransform: 'none' },
					inner: { whiteSpace: 'normal', lineHeight: 1.35 },
				}}
			>
				{label}
			</Badge>
		</Box>
	)
}

type IndicatorProps = {
	issues: DictationLengthIssue[]
	onOpen: () => void
}

/** Stays on the page after the list popup is closed, and opens it again. */
export function DictationLengthIndicator({ issues, onOpen }: IndicatorProps) {
	if (!issues.length) return null
	return (
		<Group spacing={8}>
			<Button
				variant="light"
				color="orange"
				size="xs"
				leftIcon={<IconAlertTriangle size={14} />}
				onClick={onOpen}
				aria-label="Show the Listening topics that need attention"
			>
				{dictationLengthHeading(issues)}
			</Button>
		</Group>
	)
}

type ListModalProps = {
	opened: boolean
	issues: DictationLengthIssue[]
	onClose: () => void
}

export function DictationLengthListModal({ opened, issues, onClose }: ListModalProps) {
	return (
		<Modal
			opened={opened}
			onClose={onClose}
			title={
				<Group spacing={8} noWrap>
					<IconAlertTriangle size={20} color={WARNING_COLOR} />
					<Text fw={700}>{dictationLengthHeading(issues)}</Text>
				</Group>
			}
			size={680}
			centered
		>
			<Stack spacing="sm">
				<Text size="sm">
					Learners get 15 minutes, so the English text of a Listening topic should be between{' '}
					{DICTATION_MIN_WORDS} and {DICTATION_MAX_WORDS} words. Edit the topics below to bring them
					into the range.
				</Text>
				<Box
					data-testid="dictation-length-list"
					sx={{
						maxHeight: 300,
						overflowY: 'auto',
						border: '1px solid #e6ebe7',
						borderRadius: 8,
						// The column names stay in view while the rows scroll.
						'thead th': {
							position: 'sticky',
							top: 0,
							background: '#f8faf8',
							zIndex: 1,
						},
					}}
				>
					<Table verticalSpacing={8} horizontalSpacing="sm" fontSize="sm">
						<thead>
							<tr>
								<th style={{ width: 70 }}>Day</th>
								<th>Lesson title</th>
								<th style={{ width: 100 }}>Words</th>
								<th style={{ width: 120 }}>Problem</th>
							</tr>
						</thead>
						<tbody>
							{issues.map((issue) => (
								<tr key={issue.id} data-testid="dictation-length-row">
									<td>{issue.day}</td>
									<td>{issue.title}</td>
									<td>{formatWordCount(issue.wordCount)}</td>
									<td>
										<Badge color={issue.status === 'too_long' ? 'red' : 'orange'} variant="light">
											{DICTATION_LENGTH_STATUS_LABEL[issue.status]}
										</Badge>
									</td>
								</tr>
							))}
						</tbody>
					</Table>
				</Box>
				<Text size="xs" color="dimmed">
					This is only a warning: the topics still work as they are. The orange warning on the page
					opens this list again.
				</Text>
				<Group position="right">
					<Button variant="default" onClick={onClose}>
						Close
					</Button>
				</Group>
			</Stack>
		</Modal>
	)
}

type SaveModalProps = {
	wordCount: number
	onSaveAnyway: () => void
	onGoBack: () => void
}

/** Asked at save time when the text is outside the range. Saving stays possible. */
export function DictationLengthSaveModal({ wordCount, onSaveAnyway, onGoBack }: SaveModalProps) {
	const tooLong = classifyDictationLength(wordCount) === 'too_long'
	return (
		<Modal
			opened
			onClose={onGoBack}
			title={
				<Group spacing={8} noWrap>
					<IconAlertTriangle size={20} color={WARNING_COLOR} />
					<Text fw={700}>{tooLong ? 'The English text is too long' : 'The English text is too short'}</Text>
				</Group>
			}
			size="md"
			centered
		>
			<Stack spacing="sm">
				<Text size="sm">
					This Listening text has <strong>{formatWordCount(wordCount)}</strong>. The allowed range is{' '}
					<strong>
						{DICTATION_MIN_WORDS} to {DICTATION_MAX_WORDS} words
					</strong>
					, because learners get 15 minutes.
				</Text>
				<Text size="xs" color="dimmed">
					You can still save it as it is.
				</Text>
				<Group position="right">
					<Button variant="default" onClick={onGoBack} data-autofocus>
						Go back and edit
					</Button>
					<Button color="orange" onClick={onSaveAnyway}>
						Save anyway
					</Button>
				</Group>
			</Stack>
		</Modal>
	)
}
